import { attach, combine, createApi, createEvent, createStore, sample, split } from 'effector'
import { Status } from './lib/utils'
import { StepId } from '../lib/utils'
import {
  executeStepFx,
  stepChanged,
  $codeExpirationTime,
  codeExpirationLoaded,
  $user,
  $userId,
} from '../onboarding.model'
import { emailSubmitted } from '../verificar-email/verificar-email.model'
import dayjs from 'dayjs'

export const codeChanged = createEvent()
export const codeCleared = createEvent()
export const codeSubmitted = createEvent()
export const statusChanged = createEvent()
export const newCodeRequested = createEvent()
export const timeLeftChanged = createEvent()

export const $value = createStore('')
export const $disabled = createStore(false)
export const $status = createStore(Status.IDLE)
export const $error = createStore(null)
export const $timeLeft = createStore(0)

const $body = combine([$value, $user, $userId], ([codigo, usuario, userId]) => ({
  step: StepId.VERIFICATION_CODE,
  payload: {
    codigo: parseInt(codigo),
    weid: +userId,
  },
}))

export const validateCodeFx = attach({
  source: $body,
  effect: executeStepFx,
})

$value.reset(codeCleared)
$disabled.reset(codeCleared)

$value.on(codeChanged, (_, value) => value)
$disabled.on(codeChanged, (_, value) => value === '')
$disabled.on(statusChanged, (_, status) => [Status.INVALID, Status.EXPIRED].includes(status))
$status.on(statusChanged, (_, status) => status)

// Reset status y error
sample({
  clock: codeCleared,
  source: $status,
  filter: (status) => [Status.INVALID, Status.EXPIRED, Status.ERROR].includes(status),
  fn: () => Status.IDLE,
  target: statusChanged,
})

sample({
  clock: codeCleared,
  source: $status,
  filter: (status) => [Status.INVALID, Status.EXPIRED, Status.ERROR].includes(status),
  target: $error.reinit,
})

// Enviar código de verificación
sample({
  clock: codeSubmitted,
  target: validateCodeFx,
})

split({
  source: validateCodeFx.failData,
  match: {
    expired: (error) => {
      return error.code === 'EXPIRED_CODE'
    },
    invalid: (error) => error.code === 'INVALID_CODE',
    error: (error) => error.code === 'SERVICE_UNAVAILABLE',
  },
  cases: createApi($status, {
    expired: () => Status.EXPIRED,
    invalid: () => Status.INVALID,
    error: () => Status.ERROR,
  }),
})

sample({
  clock: validateCodeFx.failData,
  fn: (error) => ({ title: error.title, message: error.message }),
  target: $error,
})

sample({
  clock: validateCodeFx.done,
  fn: () => StepId.NEW_PASSWORD,
  target: [stepChanged, codeCleared],
})

// Solicitar nuevo código
sample({
  clock: newCodeRequested,
  target: [emailSubmitted, $value.reinit],
})

sample({
  clock: newCodeRequested,
  fn: () => Status.REVALIDATE,
  target: $status,
})

sample({
  clock: timeLeftChanged,
  source: $timeLeft,
  filter: (timeLeft) => timeLeft > 0,
  fn: (timeLeft) => timeLeft - 1,
  target: $timeLeft,
})

// Calcular tiempo restante al cargar la expiración
sample({
  clock: codeExpirationLoaded,
  source: $codeExpirationTime,
  fn: (expirationTime) => (expirationTime ? dayjs(expirationTime).diff(dayjs(), 'second') : 0),
  target: $timeLeft,
})

sample({
  clock: $codeExpirationTime,
  fn: (expirationTime) => (expirationTime ? dayjs(expirationTime).diff(dayjs(), 'second') : 0),
  target: $timeLeft,
})
